import React, { useState, useEffect } from 'react';
import { Bell, X, Check, CheckCheck, Mail, Megaphone, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  bienvenida: { icon: '👋', color: 'text-green-400' },
  anuncio: { icon: '📢', color: 'text-[#D4AF37]' },
  actualizacion: { icon: '🔄', color: 'text-blue-400' },
  cambio_estado: { icon: '📝', color: 'text-yellow-400' },
  recordatorio: { icon: '⏰', color: 'text-purple-400' },
  operativo: { icon: '⚙️', color: 'text-gray-400' },
  correccion: { icon: '✏️', color: 'text-orange-400' },
  promocion: { icon: '🎉', color: 'text-pink-400' }
};

export const NotificationBell = () => {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.items || []);
      setUnreadCount(response.data.unread || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 top-full mt-2 w-96 max-h-[500px] glass-dark rounded-xl border border-white/10 overflow-hidden z-50"
            >
              {/* Header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell size={18} className="text-[#D4AF37]" />
                  <h3 className="text-white font-medium">Notificaciones</h3>
                  {unreadCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs">
                      {unreadCount} nuevas
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-[#D4AF37] text-xs hover:underline flex items-center gap-1"
                    >
                      <CheckCheck size={14} /> Marcar todo
                    </button>
                  )}
                  <button
                    onClick={fetchNotifications}
                    className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                  >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Notifications List */}
              <div className="max-h-[400px] overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell size={32} className="text-white/20 mx-auto mb-3" />
                    <p className="text-white/50 text-sm">No hay notificaciones</p>
                  </div>
                ) : (
                  notifications.map((notification) => {
                    const typeInfo = typeIcons[notification.type] || typeIcons.anuncio;
                    
                    return (
                      <div
                        key={notification.notification_id}
                        onClick={() => !notification.is_read && markAsRead(notification.notification_id)}
                        className={`p-4 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${
                          !notification.is_read ? 'bg-white/5' : ''
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-lg">
                            {typeInfo.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className={`text-sm font-medium truncate ${notification.is_read ? 'text-white/70' : 'text-white'}`}>
                                {notification.subject}
                              </h4>
                              {!notification.is_read && (
                                <span className="w-2 h-2 rounded-full bg-[#D4AF37] flex-shrink-0 mt-1.5" />
                              )}
                            </div>
                            <p className="text-white/50 text-xs mt-1 line-clamp-2">
                              {notification.content}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-white/30 text-xs">
                                {notification.sender_name}
                              </span>
                              <span className="text-white/20">•</span>
                              <span className="text-white/30 text-xs">
                                {formatTime(notification.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// Full Notifications Page Component
export const NotificationsPanel = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.items || []);
      setUnreadCount(response.data.unread || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [token]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <div className="glass-dark rounded-xl p-8 text-center">
        <RefreshCw size={24} className="text-white/40 mx-auto animate-spin mb-2" />
        <p className="text-white/50">Cargando notificaciones...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl text-white mb-1">Notificaciones</h2>
          <p className="text-white/50 text-sm">
            {unreadCount > 0 ? `${unreadCount} sin leer` : 'Todas leídas'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="btn-secondary text-sm flex items-center gap-2"
          >
            <CheckCheck size={16} /> Marcar todo como leído
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="glass-dark rounded-xl p-12 text-center">
          <Bell size={48} className="text-white/20 mx-auto mb-4" />
          <h3 className="text-white font-display text-xl mb-2">Sin notificaciones</h3>
          <p className="text-white/50">No tienes notificaciones aún</p>
        </div>
      ) : (
        <div className="glass-dark rounded-xl overflow-hidden divide-y divide-white/5">
          {notifications.map((notification) => {
            const typeInfo = typeIcons[notification.type] || typeIcons.anuncio;
            
            return (
              <div
                key={notification.notification_id}
                onClick={() => !notification.is_read && markAsRead(notification.notification_id)}
                className={`p-6 hover:bg-white/5 transition-colors cursor-pointer ${
                  !notification.is_read ? 'bg-white/5' : ''
                }`}
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-2xl">
                    {typeInfo.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className={`font-medium ${notification.is_read ? 'text-white/70' : 'text-white'}`}>
                          {notification.subject}
                        </h3>
                        <p className="text-white/50 text-sm mt-1">
                          {notification.content}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <span className="w-3 h-3 rounded-full bg-[#D4AF37] flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-white/40">
                      <span>De: {notification.sender_name}</span>
                      <span>•</span>
                      <span>{new Date(notification.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
